<script setup lang="ts">
import {computed, inject, reactive, ref} from "vue";
import {useRoute} from "vue-router";
import Str from "@/Utils/Str";
import {getAudits as getAuditsFromApi} from "@/Services/Admin/admin-audits";
import {Button, Column, DataTable, Dialog, MultiSelect, Tag} from "@/Components/Library";
import {PageHeading} from "@/Components/Global";
import type dayjs from "dayjs";
import {AdminIndex} from "@/Types/index.d";
import {FilterMatchMode} from "@primevue/core/api";
import {getUsers} from "@/Services/Admin/admin-users";

type AuditLocal = any;

const currentRoute = useRoute();
const $date = inject("date") as typeof dayjs;
const audits = ref<AdminIndex<AuditLocal>>();
const users = ref([]);

const state = reactive({
    show: {
        changesModal: false,
    },
    selectedAudit: null,
});

const selectedAuditsKeys = computed(() => {
    if (!state.selectedAudit) {
        return [];
    }

    return Object.keys(state.selectedAudit.new_values).length
        ? Object.keys(state.selectedAudit.new_values)
        : Object.keys(state.selectedAudit.old_values);
});

const filters = ref({
    user_id: {
        value: [],
        matchMode: FilterMatchMode.IN,
    },
});

const linkableModels = ["users", "groups", "questions"];

const getAudits = async () =>
    getAuditsFromApi({
        ...currentRoute.query,
        include: ["user", "auditable"],
    }).then((data) => (audits.value = data));
getAudits();
getUsers().then(({data}) => (users.value = data));

const viewChanges = (audit: AuditLocal) => {
    state.selectedAudit = audit;
    state.show.changesModal = true;
};
</script>
<template>
    <div>
        <div class="m-container">
            <PageHeading title="Audits" />
        </div>
        <DataTable
            :data="audits"
            dataKey="id"
            :loading="false"
            :filters="filters"
            @filter="getAudits"
            defaultSort="-created_at"
        >
            <Column expander style="width: 0" />
            <Column field="id" header="ID" />
            <Column field="user_id" header="User" :sortable="true" :showFilterMatchModes="false">
                <template #body="{data}">
                    <RouterLink
                        v-if="data.user_id"
                        :to="{name: 'admin.users.show', params: {user: data.user_id}}"
                        class="hover:underline"
                    >
                        {{ data.user.name }}
                    </RouterLink>
                    <span v-else>SYSTEM</span>
                </template>
                <template #filter="{filterModel, filterCallback}">
                    <MultiSelect
                        v-model="filterModel.value"
                        @change="filterCallback"
                        :options="users"
                        option-value="id"
                        option-label="name"
                        placeholder="Select users"
                        filter
                        class="p-column-filter"
                        style="min-width: 14rem"
                        :maxSelectedLabels="1"
                    />
                </template>
                <template #filterclear="{filterCallback}" />
                <template #filterapply="{filterCallback}" />
            </Column>
            <Column field="event" header="Event" :sortable="true">
                <template #body="{data}">
                    <Tag
                        :value="data.event"
                        :severity="
                            {
                                created: 'success',
                                updated: 'warn',
                                deleted: 'danger',
                            }[data.event]
                        "
                    />
                </template>
            </Column>
            <Column field="auditable" header="Auditable">
                <template #body="{data}">
                    <RouterLink
                        v-if="data.auditable && linkableModels.includes(data.auditable_plural)"
                        :to="{
                            name: `admin.${data.auditable_plural}.show`,
                            params: {[data.auditable_type]: data.auditable_id},
                        }"
                        class="hover:underline"
                    >
                        <span class="font-bold">{{ Str.of(data.auditable_type).title() }}</span>
                        {{ data.auditable.name ?? data.auditable.title ?? data.auditable_id }}
                    </RouterLink>
                    <p v-else>
                        <span class="font-bold">{{ Str.of(data.auditable_type).title() }}</span>
                        {{ data.auditable?.name ?? data.auditable?.title ?? data.auditable_id }}
                    </p>
                </template>
            </Column>
            <Column field="created_at" header="Date" :sortable="true">
                <template #body="{data}">
                    {{ $date(data.created_at).format("DD/MM/YYYY HH:mm") }}
                </template>
            </Column>
            <Column header="Actions" style="width: 0">
                <template #body="{data}">
                    <Button
                        icon="clarity:eye-solid"
                        class="h-8 w-8 bg-askr-green text-white"
                        @click="viewChanges(data)"
                    />
                </template>
            </Column>
            <template #expansion="{data}">
                <div class="flex flex-wrap items-start gap-6">
                    <table class="mb-6">
                        <thead>
                            <tr>
                                <th class="p-2">Attribute</th>
                                <th class="p-2">From</th>
                                <th class="p-2">To</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(val, key, i) in data.new_values" :key="key">
                                <td class="p-2" :class="[i % 2 ? '' : 'bg-stone-100 dark:bg-stone-950']">{{ key }}</td>
                                <td class="p-2" :class="[i % 2 ? '' : 'bg-stone-100 dark:bg-stone-950']">
                                    {{ data.old_values?.[key] ?? "" }}
                                </td>
                                <td class="p-2" :class="[i % 2 ? '' : 'bg-stone-100 dark:bg-stone-950']">{{ val }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <pre class="whitespace-pre-wrap break-words">{{ data.auditable }}</pre>
                </div>
            </template>
        </DataTable>
        <Dialog
            v-model:visible="state.show.changesModal"
            modal
            :header="`${state.selectedAudit?.auditable_type} ${state.selectedAudit?.event} - ${state.selectedAudit?.auditable?.name ?? state.selectedAudit?.auditable?.title ?? state.selectedAudit?.auditable_id}`"
            class="mx-8 max-h-dvh w-full overflow-y-auto sm:max-w-3xl"
        >
            <table class="mb-6">
                <thead>
                    <tr>
                        <th class="p-2">Attribute</th>
                        <th class="p-2">From</th>
                        <th class="p-2">To</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(val, key, i) in state.selectedAudit.new_values" :key="key">
                        <td class="p-2" :class="[{'bg-stone-100 dark:bg-stone-950': i % 2}]">{{ key }}</td>
                        <td class="p-2" :class="[{'bg-stone-100 dark:bg-stone-950': i % 2}]">
                            {{ state.selectedAudit.old_values?.[key] ?? "" }}
                        </td>
                        <td class="p-2" :class="[{'bg-stone-100 dark:bg-stone-950': i % 2}]">{{ val }}</td>
                    </tr>
                </tbody>
            </table>
            <pre>{{ state.selectedAudit }}</pre>
        </Dialog>
    </div>
</template>
<style scoped></style>
Either way, now you do!
